/**
 * TrialStartModal — Step 0 (TRIAL_PLAN.md §8.3). The one screen that starts a
 * 14-day trial: what you get, the exact date the $15 begins, and the promise
 * that Pro tools stay free unless the creator chooses to keep them.
 *
 * Card is captured by Stripe Checkout (D2); nothing is charged today.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTrial, ackTrial, money, trialDate } from "@/lib/trial";
import TrialCreditNote from "@/components/TrialCreditNote";

const PERKS = [
  "Every Creator Pro tool, unlocked — Workbench, Refinery, YouTube replies, Calendar, Silo Wars",
  "Up to 3 silos, your library, Tone Architect, Public Q&A, fan subscriptions and tips",
  "Your AI calls run on trial credit at cost — no markup while the trial lasts",
];

export default function TrialStartModal({ open, onOpenChange }) {
  const { trial } = useTrial();
  const [busy, setBusy] = useState(false);
  const days = trial?.trial_days || 14;
  const chargeDate = trialDate(
    new Date(Date.now() + days * 86400000).toISOString(),
  );

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trial/start", { origin_url: window.location.origin });
      if (r.data?.already_trialing) {
        toast.success("Your trial is already running.");
        onOpenChange(false);
        return;
      }
      if (r.data?.url) { window.location.href = r.data.url; return; }
      throw new Error("no url");
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Could not start your trial.");
    }
  };

  const decline = async () => {
    await ackTrial("step0");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) decline(); }}>
      <DialogContent
        className="max-w-lg bg-gradient-to-br from-[#1C1B1A] to-[#2C3B2F] text-[#FDFCF8] border-0 overflow-y-auto top-[calc(env(safe-area-inset-top,0px)+1rem)] translate-y-0 max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:top-1/2 sm:-translate-y-1/2 sm:max-h-[88vh]"
        data-testid="trial-start-modal"
      >
        <DialogTitle className="sr-only">Start your 14-day trial</DialogTitle>
        <DialogDescription className="sr-only">
          Full access for {days} days. The subscription begins on {chargeDate}.
        </DialogDescription>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFC24A] flex items-center gap-2">
          <Sparkle size={12} weight="duotone" /> {days} days, everything open
        </div>
        <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mt-1">
          Build now, <em className="text-[#FFC24A]">Decide later</em>
        </h3>
        <p className="text-sm text-[#E5E5E0]/90 mt-3">
          Imagine, an AI Twin of you! How could it help the world?
        </p>

        <ul className="mt-4 space-y-2">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2 text-[13px] text-[#E5E5E0]">
              <CheckCircle size={15} weight="duotone" className="text-[#FFC24A] mt-[2px] shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4">
          <TrialCreditNote trial={trial} tone="dark" testId="trial-start-credit-note" />
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <button
            onClick={start}
            disabled={busy}
            data-testid="trial-start-confirm"
            className="flex-1 bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] px-5 py-3 rounded-md text-sm font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy ? "Opening Stripe…" : <>Start {days} days of full access <ArrowRight size={16} /></>}
          </button>
          <button
            onClick={decline}
            disabled={busy}
            data-testid="trial-start-decline"
            className="sm:w-auto border border-white/25 text-[#E5E5E0] hover:bg-white/10 px-5 py-3 rounded-md text-sm transition-colors disabled:opacity-60"
          >
            Stay on the free account
          </button>
        </div>

        <p className="text-[11px] text-[#A8A8A0] mt-3" data-testid="trial-start-fineprint">
          {money(trial?.subscription_cents || 1500)}/mo begins <strong className="text-[#E5E5E0]">{chargeDate}</strong>.
          Pro tools stay free unless you choose to keep them. Cancel any time before then and you're charged nothing.
        </p>
      </DialogContent>
    </Dialog>
  );
}
